/**
    * Pagination, search, and sorting script with localization details.
    * Pagination rules aim to:
    *   - Always show the first and last pages
    *   - Add ellipses when pages are skipped
    *   - Keep the current page centered when possible
    *
    * Example outputs:
    *   totalPages = 5   →  1 2 3 4 5
    *   totalPages = 6   →  1 2 3 4 … 6
    *   totalPages = 7   →  1 … 3 4 5 … 7
    *   totalPages = 10, currentPage = 2  →  1 2 3 4 … 10
    *   totalPages = 10, currentPage = 9  →  1 … 7 8 9 10
    *   totalPages = 20, currentPage = 10 →  1 … 9 10 11 … 20
    *
    * Html:
    * <div class="list-section">
        <input type="search" class="filter-search">
        <button class="sort-btn"></button>
        <button class="btn-filter" data-filter="all">All</button>
        <button class="btn-filter" data-filter="tap">TAP</button>
        <button class="btn-filter" data-filter="vip">VIP</button>
        <button class="sort-btn"></button>
        <div class="result-info" aria-live="polite" aria-atomic="true"></div>
        <ul class="item-list d-none" data-itemsperpage="10" data-maxpages="3" role="status" aria-atomic="true" aria-live="polite" role="region">
            <li class="item" data-type="tap"><h3>Item A</h3></li>
            <li class="item" data-type="vip"><h3>Item B</h3></li>
        </ul>
    </div>
    */
document.addEventListener('DOMContentLoaded', () => {
    // Check if global functions are avaliable
    if (typeof debounce !== 'function' || typeof pageLanguage !== 'function') {
        return;
    }

    // -------------------------------
    // Determine language and localization
    // -------------------------------
    const htmlLang = pageLanguage();
    const locales = {
        en: {
            pagination: 'Pagination of list',
            page: 'Page',
            previous: 'Previous',
            next: 'Next',
            ariaPrevious: 'Previous page',
            ariaNext: 'Next page',
            noData: 'No data found',
            showingMatches: (start, end, total) => {
                return `Showing ${start} to ${end} of ${total} matches.`;
            },
            sortAsc: 'Sort: Asc',
            sortDesc: 'Sort: Desc',
        },
        da: {
            pagination: 'Paginering af resultater',
            page: 'Side',
            previous: 'Forrige',
            next: 'Næste',
            ariaPrevious: 'Forrige side',
            ariaNext: 'Næste side',
            noData: 'Ingen resultater fundet.',
            showingMatches: (start, end, total) => {
                return `Viser ${start} til ${end} ud af ${total} resultater.`;
            },
            sortAsc: 'Sortér: Asc',
            sortDesc: 'Sortér: Desc',
        }
    };
    const locale = locales[htmlLang] || locales.en;

    // -------------------------------
    // Initialize all list sections
    // -------------------------------
    const listSections = document.querySelectorAll('.list-section');
    if (!listSections.length) {
        return;
    }

    listSections.forEach(section => {
        // -------------------------------
        // DOM elements (scoped per list)
        // -------------------------------
        const searchInput = section.querySelector('.filter-search');
        const sortBtn = section.querySelector('.sort-btn');
        const list = section.querySelector('ul.item-list');

        if (!list) {
            return;
        }

        // Remove hidden class once script is ready
        list.classList.remove('d-none');

        // Set localized UI text on sort button
        if (sortBtn) {
            sortBtn.textContent = locale.sortAsc;
        }

        // Create pagination container
        const paginationDiv = document.createElement('div');
        paginationDiv.className = 'filter-pagination';

        const infoDiv = section.querySelector('.result-info');

        list.parentNode.insertBefore(paginationDiv, list.nextSibling);

        // Cache all list items
        const allItems = Array.from(list.querySelectorAll('li.item'));

        // Initialize state per list
        let filteredItems = [...allItems];
        let currentPage = 1; // Current page number
        let sortAscending = true; // Sort state: ascending/descending
        let activeTypeFilter = 'all'; // Set default filter

        // -------------------------------
        // Helper Functions (scoped)
        // -------------------------------

        /**
         * Retrieve a data attribute value and parse it into an integer, with a fallback default.
         * @param {string} key The data attribute key (without 'data-')
         * @param {number} fallbackValue The fallback default value if not found or invalid.
         * @returns {number} The parsed integer value or the default.
         */
        function getDomData(key, fallbackValue) {
            const value = list.dataset[key];
            const parsed = parseInt(value, 10);
            if (!isNaN(parsed)) {
                return parsed;
            } else {
                return fallbackValue;
            }
        }

        // Number of items in list per paginated page
        const rawItemsPerPage = getDomData('itemsperpage', 10);
        const itemsPerPage = rawItemsPerPage === 0 ? 0 : Math.max(1, rawItemsPerPage);

        // How many page numbers (including current item) are visible before ellipses
        const maxVisiblePages = getDomData('maxpages', 3);

        /**
         * Robust search matching with full Unicode letters:
         * - Multi-letter terms: match start of any word (partial match)
         * - Single-letter terms: match word initials only
         * - Keeps accented and special letters
         * - Splits words by spaces or dashes so "Bruun-Rasmussen" works
         */
        function matchesAllTerms(text, terms) {
            // Normalize text to decompose accents (NFKD) and lowercase
            const normalizedText = text.normalize('NFKD').toLowerCase();

            // Split into words by spaces or dashes, then remove non-letter characters
            const words = normalizedText
                .split(/[\s-]+/) // split by spaces or dashes
                .map(word => word.replace(/[^\p{L}]/gu, '')) // keep only letters
                .filter(Boolean);

            return terms.every(term => {
                // Normalize term the same way
                const normalizedTerm = term
                    .normalize('NFKD')
                    .toLowerCase()
                    .replace(/[^\p{L}]/gu, '');

                if (!normalizedTerm) return false; // skip empty terms

                if (normalizedTerm.length === 1) {
                    // Single-letter term → match first character of a word
                    return words.some(word => word[0] === normalizedTerm);
                } else {
                    // Multi-letter term → match start of any word
                    return words.some(word => word.startsWith(normalizedTerm));
                }
            });
        }

        /**
         * Filter items by search query.
         * Matches exact characters, including accents.
         * @param {string} query
         */
        function filterItems(query) {
            // Trim whitespace, convert to lowercase, and normalize to NFC for consistent Unicode handling
            const normalizedQuery = query.trim().toLowerCase().normalize('NFC');
            // Split the search query into individual words (space-separated) and remove empty entries
            const searchTerms = normalizedQuery.split(/\s+/).filter(Boolean);

            filteredItems = allItems.filter(item => {

                // Normalize the item's text content to NFC and convert to lowercase
                const itemText = item.textContent.toLowerCase().normalize('NFC');

                // Check if all words from the search input exist somewhere in the item's text
                const matchesText = matchesAllTerms(itemText, searchTerms);

                // Check if the item matches the currently active type filter
                const matchesType =
                    activeTypeFilter === 'all' || // 'all' matches everything
                    item.dataset.type === activeTypeFilter; // otherwise match specific type

                // Include item only if it matches both the text search and type filter
                return matchesText && matchesType;
            });
            currentPage = 1;
            renderList();
            renderPagination();
            renderInfo();
        }

        /**
         * Filter items by type (tap, vip, or all)
         * @param {string} type
         */
        function filterByType(type) {
            // Store the currently active type filter
            activeTypeFilter = type;

            // Filter all items based on the selected type
            filteredItems = allItems.filter(item => {

                // If "all" is selected, include every item
                if (activeTypeFilter === 'all') {
                    return true;
                }

                // Otherwise, only include items matching the selected data-type
                return item.dataset.type === activeTypeFilter;
            });

            currentPage = 1;
            renderList();
            renderPagination();
            renderInfo();
        }

        /**
         * Render list items for current page.
         * Shows message if no items.
         */
        function renderList() {
            // Hide all list items before displaying the relevant ones
            allItems.forEach(item => {
                item.classList.add('d-none');
            });

            // Remove any previous "no data" messages
            const oldNoData = list.querySelector('.result-no-data');
            if (oldNoData) {
                oldNoData.remove();
            }

            // If there are no matching items, show "No data"
            if (filteredItems.length === 0) {
                const noDataLi = document.createElement('li');
                noDataLi.className = 'result-no-data';
                const div = document.createElement('div');
                div.className = 'alert alert-info';
                div.textContent = locale.noData;
                div.setAttribute('role', 'alert');
                noDataLi.appendChild(div);
                list.appendChild(noDataLi);
                return; // Exit early — nothing else to show
            } else {
                // Calculate which items belong to the current page
                // If pagination is disabled, show all items
                if (!itemsPerPage) {
                    filteredItems.forEach(item => {
                        item.classList.remove('d-none');
                    });
                    return;
                }

                // Paginated rendering
                const start = (currentPage - 1) * itemsPerPage; // First item index
                const end = Math.min(start + itemsPerPage, filteredItems.length); // Last item index (safe limit)

                // Loop through and display only the items for this page
                for (let i = start; i < end; i++) {
                    filteredItems[i].classList.remove('d-none'); // Unhide the visible items
                }
            }
        }

        /**
         * Render pagination with ellipsis.
         *
         * Rules:
         *  - Always show the first and last page
         *  - Keep the current page centered when possible
         *  - Collapse long ranges using ellipses
         *  - Number of visible pages is configurable via `maxVisiblePages`
         *
         * Uses a single DocumentFragment to minimize DOM updates.
         * Previous/Next buttons are only shown when applicable.
         */
        function renderPagination() {
            // Clear any existing pagination
            paginationDiv.innerHTML = '';

            if (itemsPerPage === 0) {
                return; // Pagination disabled
            }
            if (filteredItems.length === 0) {
                return; // No items, no pagination
            }

            const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
            if (totalPages <= 1) {
                return; // Only one page, nothing to paginate
            }

            const nav = document.createElement('nav');
            nav.setAttribute('aria-label', locale.pagination);

            const ul = document.createElement('ul');
            ul.className = 'pagination';

            // Use a document fragment to avoid excessive reflows
            const fragment = document.createDocumentFragment();

            // -------------------
            // Previous button
            // -------------------
            if (currentPage > 1) {
                fragment.appendChild(
                    createPageItem(
                        locale.previous,
                        currentPage - 1,
                        locale.ariaPrevious,
                        'previous',
                        false
                    )
                );
            }

            // -------------------
            // Determine page numbers & ellipses
            // -------------------
            const pagesToRender = [];

            // Number of pages shown on each side of the current page.
            // Best UX when maxVisiblePages is odd (e.g. 5, 7, 9),
            // but even numbers will still work.
            const half = Math.floor(maxVisiblePages / 2);

            // CASE 1: Total pages fit within the visible limit
            // → Show all pages, no ellipses needed
            if (totalPages <= maxVisiblePages) {
                for (let i = 1; i <= totalPages; i++) {
                    pagesToRender.push(i);
                }
            } else {
                // CASE 2: Large page sets → use ellipses
                // Always show the first page
                pagesToRender.push(1);

                // --- Subcase A: Current page near the start
                // Example: 1 2 3 … 10
                if (currentPage <= half + 1) {
                    for (let i = 2; i <= maxVisiblePages; i++) {
                        pagesToRender.push(i);
                    }
                    pagesToRender.push('ellipsis');
                    pagesToRender.push(totalPages);
                }

                // --- Subcase B: Current page near the end
                // Example: 1 … 8 9 10
                else if (currentPage >= totalPages - half) {
                    pagesToRender.push('ellipsis');
                    for (
                        // Subtract 1 to account for:
                        //  - last page (1)
                        //  - ellipsis
                        let i = totalPages - (maxVisiblePages - 1);
                        i <= totalPages;
                        i++
                    ) {
                        pagesToRender.push(i);
                    }
                }

                // --- Subcase C: Current page somewhere in the middle
                // Example: 1 … 9 10 11 … 20
                else {
                    // Add the first ellipsis after the first page to indicate skipped pages
                    pagesToRender.push('ellipsis');

                    // Loop through pages centered around the current page
                    // `half` determines how many pages to show on each side
                    for (
                        let i = currentPage - half; // start from `half` pages before current
                        i <= currentPage + half;     // end at `half` pages after current
                        i++
                    ) {
                        // Add each page number to the render list
                        pagesToRender.push(i);
                    }

                    // Add the second ellipsis to indicate skipped pages before the last page
                    pagesToRender.push('ellipsis');

                    // Always show the last page number
                    pagesToRender.push(totalPages);
                }
            }

            // -------------------
            // Render page buttons & ellipses
            // -------------------
            pagesToRender.forEach(pageOrEllipsis => {
                if (pageOrEllipsis === 'ellipsis') {
                    fragment.appendChild(createEllipsis());
                } else {
                    fragment.appendChild(
                        createPageItem(
                            pageOrEllipsis,
                            pageOrEllipsis,
                            false,
                            '',
                            currentPage === pageOrEllipsis
                        )
                    );
                }
            });

            // -------------------
            // Next button
            // -------------------
            if (currentPage < totalPages) {
                fragment.appendChild(
                    createPageItem(
                        locale.next,
                        currentPage + 1,
                        locale.ariaNext,
                        'next',
                        false
                    )
                );
            }

            // Final DOM append
            ul.appendChild(fragment);
            nav.appendChild(ul);
            paginationDiv.appendChild(nav);
        }

        /**
         * Render info text: "Showing X to Y of Z matches"
         */
        function renderInfo() {
            if (!infoDiv) {
                return;
            }

            if (filteredItems.length === 0) {
                infoDiv.textContent = '';
                return;
            }

            const start = (currentPage - 1) * itemsPerPage + 1;
            const end = Math.min(currentPage * itemsPerPage, filteredItems.length);
            infoDiv.textContent = locale.showingMatches(start, end, filteredItems.length);
            infoDiv.setAttribute('aria-label', `${locale.page} ${currentPage} / ${Math.ceil(filteredItems.length / itemsPerPage)}`);
        }

        /**
         * Create ellipsis element for pagination
         */
        function createEllipsis() {
            const li = document.createElement('li');
            li.className = 'page-item-dots';
            const div = document.createElement('div');
            div.textContent = '...';
            li.appendChild(div);
            return li;
        }

        /**
        * Creates a pagination button element for navigation.
        *
        * @param {string} text - The visible text of the pagination button (e.g., "Previous", "Next", or page number).
        * @param {number} page - The target page number this button navigates to when clicked.
        * @param {string|boolean} [ariaLabel=false] - Optional ARIA label for accessibility.
        * @param {string} [extraClass=''] - Optional extra CSS class to apply (e.g., "prev-btn" or "next-btn").
        * @param {boolean} [active=false] - Whether this button represents the currently active page.
        * @returns {HTMLLIElement} The fully constructed `<li>` pagination element.
        */
        function createPageItem(text, page, ariaLabel = false, extraClass = '', active = false) {
            const li = document.createElement('li');
            li.className = 'page-item';

            if (extraClass) {
                li.classList.add(extraClass);
            }
            if (active) {
                li.classList.add('active');
            }

            const a = document.createElement('a');
            a.className = 'page-link';
            a.href = '#';

            // If this is a Previous or Next button, wrap text in a span
            if (extraClass === 'previous' || extraClass === 'next') {
                const span = document.createElement('span');
                span.className = 'visually-hidden';
                span.textContent = text;
                a.appendChild(span);
            } else {
                // Normal numbered page
                a.textContent = text;
            }

            // Set aria-label if provided
            if (ariaLabel) {
                a.setAttribute('aria-label', ariaLabel);
            }
            // Set aria-current if this is the active page
            if (active) {
                a.setAttribute('aria-current', 'page');
            }

            // Click event for mouse
            a.addEventListener('click', e => {
                e.preventDefault();
                goToPage(page);
            });

            li.appendChild(a);
            return li;
        }

        /**
         * Navigate to a specific page in the paginated list.
         *
         * Updates the current page, re-renders the visible list items,
         * updates pagination buttons, and refreshes the info text.
         *
         * @param {number} page - The page number to navigate to (1-based index).
         */
        function goToPage(page) {
            // Calculate the total number of pages based on filtered items
            const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
            // If the requested page is out of bounds, do nothing
            if (page < 1 || page > totalPages) {
                return;
            }
            // Update the current page state
            currentPage = page;
            // Render the items visible on the current page
            renderList();
            renderPagination();
            renderInfo();

            // -------------------------
            // Update aria-live for screen readers
            // -------------------------
            if (infoDiv) {
                // Add current page info so screen readers announce the page change
                infoDiv.setAttribute(
                    'aria-label',
                    `${locale.page} ${currentPage} / ${totalPages}`
                );
            }
        }

        /**
         * Toggle sort order and update list
         */
        if (sortBtn) {
            sortBtn.addEventListener('click', () => {
                sortAscending = !sortAscending;
                if (sortAscending) {
                    sortBtn.textContent = locale.sortAsc;
                } else {
                    sortBtn.textContent = locale.sortDesc;
                }

                filteredItems.sort((a, b) => {
                    const aText = a.querySelector('h3').textContent.trim();
                    const bText = b.querySelector('h3').textContent.trim();
                    if (sortAscending) {
                        return aText.localeCompare(bText, htmlLang);
                    } else {
                        return bText.localeCompare(aText, htmlLang);
                    }
                });
                // Render the items visible on the current page
                renderList();
                renderPagination();
                renderInfo();
            });
        }

        filterItems('');

        // Hide spinner after first render
        const spinner = section.querySelector('.ucph-spinner');
        if (spinner) {
            spinner.classList.add('d-none');
        }

        // Filter buttons
        const filterButtons = section.querySelectorAll('.btn-filter');

        if (filterButtons.length) {
            filterButtons.forEach((btn) => {
                // Ensure active filter button is skipped when tabbing
                if (btn.getAttribute('aria-pressed') === 'true') {
                    btn.setAttribute('tabindex', '-1');
                } else {
                    btn.setAttribute('tabindex', '0');
                }
                btn.addEventListener('click', () => {
                    // Remove "active" class and reset aria-pressed from all filter buttons
                    filterButtons.forEach((b) => {
                        b.classList.remove('active');
                        b.setAttribute('aria-pressed', 'false');
                        b.setAttribute('tabindex', '0');
                    });

                    // Mark the clicked button as active and aria-pressed
                    btn.classList.add('active');
                    btn.setAttribute('aria-pressed', 'true');
                    btn.setAttribute('tabindex', '-1');

                    // Apply the selected type filter (tap, vip, or all)
                    filterByType(btn.dataset.filter);
                });
            });
        }

        if (searchInput) {
            // Search input listener
            searchInput.addEventListener('input', debounce(() => {
                filterItems(searchInput.value.trim());
            }, 200));
        }
    });
});
